import { supabase } from '../supabaseClient';
import type {
  EvaluationLevelDisplayLabelsByStepCount,
  EvaluationLevelStepCount,
  WorkspaceEvaluationPlanLevelSettings,
} from '../types/evaluationPlan';
import {
  DEFAULT_LEVEL_STEP_COUNT,
  clampLevelStepCount,
  emptyWorkspaceEvaluationPlanLevelSettings,
  levelDisplayLabelsByStepCountToDbPayload,
  parseEvaluationLevelDisplayLabelsByStepCount,
} from '../types/evaluationPlan';
import {
  loadWorkspaceEvaluationPlanLevelSettingsCache,
  saveWorkspaceEvaluationPlanLevelSettingsCache,
} from '../utils/workspaceEvaluationPlanLevelSettingsCache';

function requireWorkspaceId(workspaceId: string): string {
  const ws = workspaceId.trim();
  if (!ws) throw new Error('워크스페이스를 선택해 주세요.');
  return ws;
}

function requireGradeId(gradeId: string): string {
  const gid = gradeId.trim();
  if (!gid) throw new Error('클래스를 선택해 주세요.');
  return gid;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw new Error(error.message);
  const uid = data.user?.id;
  if (!uid) throw new Error('로그인이 필요합니다.');
  return uid;
}

function isMissingLevelSettingsTableError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('workspace_evaluation_plan_level_settings') &&
    (lower.includes('schema cache') || lower.includes('does not exist') || lower.includes('could not find'))
  );
}

function isMissingDefaultStepColumnError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('default_level_step_count') &&
    (lower.includes('column') || lower.includes('schema') || lower.includes('does not exist'))
  );
}

function normalizeSettings(raw: {
  labels_by_step?: unknown;
  default_level_step_count?: unknown;
}): WorkspaceEvaluationPlanLevelSettings {
  return {
    defaultLevelStepCount: clampLevelStepCount(raw.default_level_step_count ?? DEFAULT_LEVEL_STEP_COUNT),
    labelsByStep: raw.labels_by_step
      ? parseEvaluationLevelDisplayLabelsByStepCount(raw.labels_by_step)
      : emptyWorkspaceEvaluationPlanLevelSettings().labelsByStep,
  };
}

export async function getWorkspaceEvaluationPlanLevelSettings(
  workspaceId: string,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<WorkspaceEvaluationPlanLevelSettings> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const empty = emptyWorkspaceEvaluationPlanLevelSettings();
  if (!options?.forceRefresh) {
    const cached = loadWorkspaceEvaluationPlanLevelSettingsCache(ws, gid);
    if (cached.found) return cached.settings;
  }

  try {
    const uid = await requireUserId();
    let { data, error } = await supabase
      .from('workspace_evaluation_plan_level_settings')
      .select('labels_by_step, default_level_step_count')
      .eq('user_id', uid)
      .eq('workspace_id', ws)
      .eq('grade_id', gid)
      .maybeSingle();

    if (error && isMissingDefaultStepColumnError(error.message)) {
      ({ data, error } = await supabase
        .from('workspace_evaluation_plan_level_settings')
        .select('labels_by_step')
        .eq('user_id', uid)
        .eq('workspace_id', ws)
        .eq('grade_id', gid)
        .maybeSingle());
    }

    if (error) {
      if (isMissingLevelSettingsTableError(error.message)) {
        console.warn('[evaluation-plan] level_settings 테이블 없음, 기본 A~E 사용:', error.message);
        return empty;
      }
      throw new Error(error.message);
    }

    const settings = normalizeSettings({
      labels_by_step: data?.labels_by_step,
      default_level_step_count: (data as { default_level_step_count?: unknown } | null)?.default_level_step_count,
    });

    saveWorkspaceEvaluationPlanLevelSettingsCache(ws, settings, gid);
    return settings;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (isMissingLevelSettingsTableError(message)) {
      console.warn('[evaluation-plan] level_settings 조회 실패, 기본 A~E 사용:', message);
      return empty;
    }
    throw e;
  }
}

export async function saveWorkspaceEvaluationPlanLevelSettings(
  workspaceId: string,
  gradeId: string,
  settings: WorkspaceEvaluationPlanLevelSettings
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const uid = await requireUserId();
  const labelsByStep = parseEvaluationLevelDisplayLabelsByStepCount(settings.labelsByStep);
  const defaultLevelStepCount = clampLevelStepCount(settings.defaultLevelStepCount);
  const basePayload = {
    user_id: uid,
    workspace_id: ws,
    grade_id: gid,
    labels_by_step: levelDisplayLabelsByStepCountToDbPayload(labelsByStep),
    updated_at: new Date().toISOString(),
  };

  const { data: existing, error: existingErr } = await supabase
    .from('workspace_evaluation_plan_level_settings')
    .select('id')
    .eq('user_id', uid)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .maybeSingle();
  if (existingErr) {
    if (isMissingLevelSettingsTableError(existingErr.message)) {
      throw new Error('성취수준 표시 설정 테이블이 아직 준비되지 않았습니다. 잠시 후 다시 시도하거나 관리자에게 문의해 주세요.');
    }
    throw new Error(existingErr.message);
  }

  const withDefault = { ...basePayload, default_level_step_count: defaultLevelStepCount };

  if (existing?.id) {
    let { error } = await supabase
      .from('workspace_evaluation_plan_level_settings')
      .update(withDefault)
      .eq('id', existing.id);
    if (error && isMissingDefaultStepColumnError(error.message)) {
      ({ error } = await supabase
        .from('workspace_evaluation_plan_level_settings')
        .update(basePayload)
        .eq('id', existing.id));
    }
    if (error) throw new Error(error.message);
  } else {
    let { error } = await supabase.from('workspace_evaluation_plan_level_settings').insert(withDefault);
    if (error && isMissingDefaultStepColumnError(error.message)) {
      ({ error } = await supabase.from('workspace_evaluation_plan_level_settings').insert(basePayload));
    }
    if (error) throw new Error(error.message);
  }

  saveWorkspaceEvaluationPlanLevelSettingsCache(
    ws,
    { defaultLevelStepCount, labelsByStep },
    gid
  );
}

export type { EvaluationLevelDisplayLabelsByStepCount, EvaluationLevelStepCount };
